import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import './TextLink.css';

/**
 * Текстовая ссылка — атом дизайн-системы.
 *
 * ## Зачем атом, если есть `<a>`
 *
 * До #1330 каждая ссылка на витрине несла свой локальный класс:
 * `.page__link`, `.site-footer__link`, `.home-showroom__link`. Все три
 * заново описывали одно и то же — фирменный цвет, ховер, зону нажатия до
 * минимума WCAG 2.5.8 AA и ветку `prefers-reduced-motion`. Три копии одного
 * правила расходятся: зона нажатия была добавлена в две из трёх, а в третьей
 * ссылка так и осталась высотой 17px.
 *
 * ## Почему не `Button`
 *
 * `Button` рендерит `<button>` и не принимает `as`. Переход на другой адрес —
 * это ссылка, а не действие: `<button>` здесь ломает Cmd+клик, «открыть в
 * новой вкладке» и объявление роли скринридером.
 *
 * ## Внешние ссылки
 *
 * `external` сам ставит `target="_blank"` и `rel="noopener noreferrer"`:
 * забытый `rel` у `target="_blank"` отдаёт открытой странице доступ к
 * `window.opener`.
 *
 * @param {object} props
 * @param {string} [props.to] — внутренний маршрут; тогда рендерится `<Link>`.
 * @param {string} [props.href] — внешний адрес; тогда рендерится `<a>`.
 * @param {boolean} [props.external] — открыть в новой вкладке (только с `href`).
 * @param {boolean} [props.arrow] — дописать стрелку после текста.
 */
export const TextLink = ({
  to,
  href,
  external = false,
  arrow = false,
  className = '',
  children,
  ...rest
}) => {
  const classes = `text-link${className ? ` ${className}` : ''}`;
  const content = (
    <>
      {children}
      {/* Стрелка — отдельный элемент, а не символ в тексте: так она не
          отрывается от последнего слова при переносе и не попадает в
          выделение при копировании подписи. */}
      {arrow && (
        <span className="text-link__arrow" aria-hidden="true">
          →
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }

  return (
    <a
      href={href}
      className={classes}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
      {...rest}
    >
      {content}
    </a>
  );
};

TextLink.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  external: PropTypes.bool,
  arrow: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default TextLink;
